import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, KeyRound, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlagsContext } from "@/hooks/useFeatureFlag";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import { ADMIN_NAV, ALWAYS_ON_KEYS, hasPermission } from "@/constants/featureRegistry";
import { ADMIN_NAV_GROUPS } from "@/constants/adminNavGroups";
import { useNavGroups } from "@/hooks/useNavGroups";
import NotificationBell from "@/components/admin/NotificationBell";
import TutorialOverlay from "@/components/tutorial/TutorialOverlay";
import { useOrderAttention, formatBadge } from "@/context/OrderAttentionContext";

export default function AdminLayout({ children }) {
  const { user, loading: authLoading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { features, loading: ffLoading, error: ffError } = useFeatureFlagsContext();
  const { subscription, loading: subLoading } = useSubscription();
  const { data: attention } = useOrderAttention();
  const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");
  // Badge per item mengikuti `item.badge` (induk Pesanan HANYA new_orders; anak Pembatalan/Refund count masing-masing)
  const badge = (item, cls) => {
    const n = item.badge ? attention?.[item.badge] || 0 : 0;
    if (n <= 0) return null;
    const tid = item.to === "/admin/pesanan" && !item.exact ? "nav-pesanan-badge" : `nav-${slug(item.label)}-badge`;
    return <span className={cls} aria-label={`${n} ${item.badgeLabel || ""}`.trim()} data-testid={tid}>{formatBadge(n)}</span>;
  };
  const isActive = (item) => (item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to));
  // Anak "exact" (Semua Pesanan) juga aktif untuk detail /admin/pesanan/:id (bukan sub-route anak lain)
  const isChildActive = (child, siblings) => (child.exact
    ? location.pathname === child.to || !siblings.some((c) => !c.exact && location.pathname.startsWith(c.to))
    : location.pathname.startsWith(child.to));

  // Gating FAIL-CLOSED: menu ter-gate hanya tampil kalau feature ENABLED.
  // Fail-open HANYA saat fetch feature flags gagal total (network outage).
  const isVisible = (item) => {
    // Multi Admin v1: menu mengikuti permission role staf (backend tetap enforce)
    if (!hasPermission(user, item.permission)) return false;
    if (!item.featureKey) return true;
    if (ALWAYS_ON_KEYS.includes(item.featureKey)) return true;
    if (ffError) return true;
    const f = features[item.featureKey];
    return !!(f && f.enabled);
  };
  const visibleNav = ADMIN_NAV.filter(isVisible);
  // IA v1: grup collapsible (presentasi) di atas ADMIN_NAV; grup aktif otomatis terbuka.
  const { entries: navEntries, isOpen: groupOpen, toggle: toggleGroup } = useNavGroups(visibleNav, ADMIN_NAV_GROUPS, isActive);

  // Gate 1: while AUTH/SUBSCRIPTION/FEATURE-FLAGS resolve, do not render children
  // (children would fetch data with wrong scope, dan menu ter-gate akan flash).
  if (authLoading || (user && ["admin", "owner"].includes(user.role) && (subLoading || ffLoading))) {
    return (
      <div
        className="admin-scope min-h-screen grid place-items-center p-4"
        style={{ backgroundColor: "var(--brand-background)" }}
        data-testid="admin-subscription-loading"
      >
        <div className="text-sm text-[#5C5C5C] font-semibold animate-pulse">Memuat dashboard…</div>
      </div>
    );
  }

  if (!user || !["admin", "owner"].includes(user.role)) {
    return (
      <div className="admin-scope min-h-screen grid place-items-center p-4" style={{ backgroundColor: "var(--brand-background)" }}>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E5E5] text-center max-w-md">
          <h2 className="font-display text-2xl font-bold mb-2">Akses Ditolak</h2>
          <p className="text-[#5C5C5C] mb-4">Anda harus login sebagai admin untuk mengakses halaman ini.</p>
          <Link to="/login"><Button className="brand-btn-primary rounded-xl">Login Admin</Button></Link>
        </div>
      </div>
    );
  }

  // Subscription lock: block ALL admin content (no children rendered → no fetch of admin/public data)
  // when tenant subscription is SUSPENDED or EXPIRED. Only SubscriptionBanner + lock notice shown.
  // Fail-open when no subscription record exists (new tenant without a plan yet).
  // Note: subLoading is already gated at the top of the component (before role check).
  const subStatus = subscription?.status;
  const isLocked = subStatus === "SUSPENDED" || subStatus === "EXPIRED";
  if (isLocked) {
    return (
      <div className="admin-scope min-h-screen flex flex-col" style={{ backgroundColor: "var(--brand-background)" }} data-testid="admin-subscription-locked">
        <header className="bg-white border-b border-[#E5E5E5] px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <Logo variant="admin" size="sm" to="/admin" />
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-xs text-[#5C5C5C]">Login sebagai</div>
              <div className="text-sm font-semibold truncate max-w-[200px]">{user.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="admin-locked-logout-btn"
            >
              <LogOut size={14} /> Keluar
            </Button>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-2xl">
            <SubscriptionBanner />
            <div
              className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E5E5] shadow-sm text-center"
              data-testid="admin-subscription-lock-notice"
            >
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-2">
                {subStatus === "EXPIRED" ? "Langganan Berakhir" : "Langganan Ditangguhkan"}
              </h2>
              <p className="text-[#5C5C5C] text-sm sm:text-base leading-relaxed">
                {subStatus === "EXPIRED"
                  ? "Masa aktif langganan tenant Anda telah berakhir. Semua fitur admin dinonaktifkan sementara untuk melindungi data toko. Silakan perbarui langganan atau hubungi Platform Owner."
                  : "Langganan tenant Anda sedang ditangguhkan oleh Platform Owner. Semua fitur admin dinonaktifkan sementara. Silakan hubungi Platform Owner untuk mengaktifkan kembali."}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item);
    return (
      <div key={item.to}>
        <Link
          to={item.to}
          data-testid={`admin-nav-${slug(item.label)}`}
          aria-current={active && !item.children ? "page" : undefined}
          aria-expanded={item.children ? active : undefined}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
            active
              ? "brand-active-menu"
              : "text-[#5C5C5C] hover:bg-[#FAF9F6] hover:text-[#1A1A1A]"
          }`}
        >
          <Icon size={18} />
          <span className="flex-1">{item.label}</span>
          {badge(item, "min-w-[22px] h-[22px] px-1.5 rounded-full brand-bg-primary text-white text-[11px] font-bold grid place-items-center")}
        </Link>
        {item.children && active && (
          <div className="ml-5 mt-1 pl-3 border-l border-[#E5E5E5] space-y-0.5" data-testid={`admin-subnav-${slug(item.label)}`}>
            {item.children.map((child) => {
              const cActive = isChildActive(child, item.children);
              return (
                <Link key={child.to} to={child.to} aria-current={cActive ? "page" : undefined}
                  data-testid={`admin-subnav-${slug(child.label)}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors ${
                    cActive ? "text-[#E85D04] bg-[#FFEDD5]/60" : "text-[#5C5C5C] hover:bg-[#FAF9F6] hover:text-[#1A1A1A]"}`}>
                  <span className="flex-1 truncate">{child.label}</span>
                  {badge(child, "min-w-[20px] h-[20px] px-1.5 rounded-full brand-bg-primary text-white text-[10px] font-bold grid place-items-center")}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-scope min-h-screen flex" style={{ backgroundColor: "var(--brand-background)" }}>
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-[#E5E5E5] sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-[#E5E5E5]">
          <Logo variant="admin" size="md" />
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-auto" data-testid="admin-sidebar-nav">
          {navEntries.map((entry) => {
            if (entry.type === "item") return renderNavItem(entry.item);
            const GIcon = entry.icon;
            const open = groupOpen(entry.key);
            const groupActive = entry.items.some(isActive);
            return (
              <div key={entry.key} className="pt-1" data-testid={`admin-nav-group-${entry.key}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(entry.key)}
                  aria-expanded={open}
                  aria-controls={`admin-nav-group-${entry.key}-items`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
                    groupActive && !open ? "text-[#1A1A1A] bg-[#FAF9F6]" : "text-[#8A8A8A] hover:text-[#1A1A1A] hover:bg-[#FAF9F6]"}`}
                  data-testid={`admin-nav-group-${entry.key}-toggle`}
                >
                  <GIcon size={15} />
                  <span className="flex-1 text-left">{entry.label}</span>
                  {!open && entry.items.some((i) => i.badge && (attention?.[i.badge] || 0) > 0) ? <span className="w-2 h-2 rounded-full brand-bg-primary" aria-hidden="true" /> : null}
                  <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open ? <div id={`admin-nav-group-${entry.key}-items`} className="mt-0.5 space-y-0.5">{entry.items.map(renderNavItem)}</div> : null}
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#E5E5E5]">
          <Link
            to="/admin/akun"
            className={`block px-3 py-2 mb-2 rounded-xl hover:bg-[#FAF9F6] ${location.pathname === "/admin/akun" ? "bg-[#FAF9F6]" : ""}`}
            title="Profil & Keamanan — ubah password"
            data-testid="admin-account-link"
          >
            <div className="text-xs text-[#5C5C5C]">Login sebagai</div>
            <div className="text-sm font-semibold truncate">{user.email}</div>
            <div className="text-xs text-[#7A5440] mt-0.5 flex items-center gap-1"><KeyRound size={12} /> Profil & Keamanan</div>
          </Link>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-[#5C5C5C] hover:bg-[#FAF9F6]"
          >
            <LogOut size={18} /> Keluar
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <Logo variant="admin" size="sm" to="/admin" />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button variant="outline" size="sm" onClick={async () => { await logout(); navigate("/"); }}>
              <LogOut size={14} /> Keluar
            </Button>
          </div>
        </header>

        {/* mobile horizontal nav */}
        <div className="md:hidden bg-white border-b border-[#E5E5E5] overflow-x-auto no-scrollbar">
          <div className="flex gap-1 p-2 min-w-max">
            {visibleNav.map((item) => {
              const active = isActive(item);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap inline-flex items-center gap-1.5 ${
                    active ? "brand-active-menu" : "text-[#5C5C5C]"
                  }`}
                >
                  {item.label}
                  {badge(item, "min-w-[18px] h-[18px] px-1 rounded-full brand-bg-primary text-white text-[10px] font-bold grid place-items-center")}
                </Link>
              );
            })}
          </div>
        </div>
        {visibleNav.filter((item) => item.children && isActive(item)).map((item) => (
          <div key={item.to} className="md:hidden bg-[#FAF9F6] border-b border-[#E5E5E5] overflow-x-auto no-scrollbar" data-testid="admin-mobile-subnav">
            <div className="flex gap-1 px-2 py-1.5 min-w-max">
              {item.children.map((child) => {
                const cActive = isChildActive(child, item.children);
                return (
                  <Link key={child.to} to={child.to} aria-current={cActive ? "page" : undefined}
                    data-testid={`admin-mobile-subnav-${slug(child.label)}`}
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap inline-flex items-center gap-1.5 border ${
                      cActive ? "bg-[#FFEDD5] text-[#E85D04] border-transparent" : "bg-white text-[#5C5C5C] border-[#E5E5E5]"}`}>
                    {child.label}
                    {badge(child, "min-w-[16px] h-[16px] px-1 rounded-full brand-bg-primary text-white text-[9px] font-bold grid place-items-center")}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        <div className="p-4 sm:p-6 lg:p-8">
          <div className="hidden md:flex justify-end mb-3">
            <NotificationBell />
          </div>
          <SubscriptionBanner />
          {children}
        </div>
      </div>
      <TutorialOverlay />
    </div>
  );
}
